import BigNumber from 'bignumber.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import { NavLink as NavLinkBase } from 'react-router-dom';

import { blackPearl } from '@paradigm/design-system/src/colors';

import { PARADEX_ROUTE } from '#/routes';

import ParadexLogoSquareSmall from '@paradigm/design-system/src/assets/ParadexLogoSquareSmall';
import BTCFilledSquare from '#/assets/coins/BTCFilledSquare';
import ETHFilledSquare from '#/assets/coins/ETHFilledSquare';
import HYPEFilledSquare from '#/assets/coins/HYPEFilledSquare';

import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

import ErrorBoundary from '#/ErrorBoundary';

import useParadexMarketSummary from './useParadexMarketSummary';
import { RawMarketSummary } from './types';

const queryClient = new QueryClient();

export default function ParadexMarketsBanner() {
  return (
    <ErrorBoundary fallback={<div />}>
      <QueryClientProvider client={queryClient}>
        <ParadexMarketsBannerContent />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function ParadexMarketsBannerContent() {
  const { status, markets } = useParadexMarketSummary();
  const { btcMarket, ethMarket, hypeMarket } = markets;

  if (status === 'loading') return <div />;
  if (status === 'error') return <div />;
  if (btcMarket == null) return <div />;
  if (ethMarket == null) return <div />;

  return (
    <NavLink
      to={PARADEX_ROUTE}
      aria-label="paradex price banner"
      title="paradex price banner"
    >
      <Market coin="BTC">
        <ParadexLogo width={18} height={18} />
        <BTCLogo width={20} height={20} />
        <DataPoint type="primary">
          <Value type="primary">{formatUsd(btcMarket.mark_price)}</Value>
        </DataPoint>
        <DataPoint type="secondary">
          <Title>24h Volume</Title>
          <Value>{volume24hUsd(btcMarket)}</Value>
        </DataPoint>
        <DataPoint type="secondary">
          <Title>OI</Title>
          <Value>{openInterestUsd(btcMarket)}</Value>
        </DataPoint>
      </Market>
      <Market coin="ETH">
        <ParadexLogo width={18} height={18} />
        <ETHLogo width={20} height={20} />
        <DataPoint type="primary">
          <Value type="primary">{formatUsd(ethMarket.mark_price)}</Value>
        </DataPoint>
        <DataPoint type="secondary">
          <Title>24h Volume</Title>
          <Value>{volume24hUsd(ethMarket)}</Value>
        </DataPoint>
        <DataPoint type="secondary">
          <Title>OI</Title>
          <Value>{openInterestUsd(ethMarket)}</Value>
        </DataPoint>
      </Market>
      {hypeMarket != null && (
        <Market coin="HYPE">
          <ParadexLogo width={18} height={18} />
          <HYPELogo width={20} height={20} />
          <DataPoint type="primary">
            <Value type="primary">{formatUsd(hypeMarket.mark_price)}</Value>
          </DataPoint>
          <DataPoint type="secondary">
            <Title>24h Volume</Title>
            <Value>{volume24hUsd(hypeMarket)}</Value>
          </DataPoint>
          <DataPoint type="secondary">
            <Title>OI</Title>
            <Value>{openInterestUsd(hypeMarket)}</Value>
          </DataPoint>
        </Market>
      )}
    </NavLink>
  );
}

const NavLink = styled(NavLinkBase)`
  height: 30px;
  display: flex;
  gap: 12px;
  flex: 1;
  width: 100%;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: transparent;

  &:hover {
    text-decoration: none;
  }
`;

const DataPoint = styled.div<{ type?: 'primary' | 'secondary' }>`
  display: flex;
  flex-direction: column;
  z-index: 1;

  ${({ type }) =>
    type === 'primary' &&
    css`
      opacity: 1;
    `}

  ${({ type }) =>
    type === 'secondary' &&
    css`
      opacity: 0.7;

      @media (max-width: 1210px) {
        display: none;
      }
    `}
`;
const Title = styled.div`
  white-space: nowrap;
  font-size: 0.6875rem;
  line-height: 0.875rem;
  opacity: 0.7;
`;
const Value = styled.div<{ type?: 'primary' }>`
  font-size: 0.6875rem;
  line-height: 0.875rem;

  ${({ type }) =>
    type === 'primary' &&
    css`
      font-size: 0.75rem;
    `}
`;

const ParadexLogo = styled(ParadexLogoSquareSmall)`
  z-index: 1;
`;
const BTCLogo = styled(BTCFilledSquare)`
  z-index: 1;
`;
const ETHLogo = styled(ETHFilledSquare)`
  z-index: 1;
`;
const HYPELogo = styled(HYPEFilledSquare)`
  z-index: 1;
`;
const Market = styled.div<{ coin?: 'BTC' | 'ETH' | 'HYPE' }>`
  display: flex;
  gap: 8px;
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  height: 30px;
  padding: 0 8px 0 4px;
  color: #ffffff;
  background-color: ${blackPearl};
  font-size: 0.75rem;
  position: relative;
  transition: all 0.25s ease;

  &::after {
    content: '';
    border-radius: 3px;
    height: 28px;
    width: calc(100% - 2px);
    position: absolute;
    top: 1px;
    left: 1px;
    background: ${blackPearl};
    z-index: 0;
    transition: all 0.25s ease;
  }

  ${({ coin }) =>
    coin === 'ETH' &&
    css`
      background: linear-gradient(90deg, ${blackPearl} 0%, #587dee 100%);
      &:hover {
        box-shadow: 0px 0px 10px 0px rgba(61, 90, 153, 0.7);
      }
      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        display: none;
      }
    `}

  ${({ coin }) =>
    coin === 'BTC' &&
    css`
      background: linear-gradient(90deg, ${blackPearl} 0%, #f3911a 100%);
      &:hover {
        box-shadow: 0px 0px 10px 0px rgba(170, 102, 18, 0.7);
      }
    `}

  ${({ coin }) =>
    coin === 'HYPE' &&
    css`
      background: linear-gradient(90deg, ${blackPearl} 0%, #4ec3a6 100%);
      &:hover {
        box-shadow: 0px 0px 10px 0px rgba(78, 195, 166, 0.7);
      }
      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        display: none;
      }
    `}
`;

function formatUsd(
  value: string | number | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  if (value == null) return '-';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'symbol',
    notation: 'standard',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  });
  return formatter.format(Number(value));
}

function openInterestUsd(marketSummary: RawMarketSummary) {
  if (marketSummary.mark_price == null) return '-';
  const openInterest = BigNumber(marketSummary.open_interest).times(
    marketSummary.mark_price,
  );
  return formatUsd(openInterest.toNumber(), { notation: 'compact' });
}

function volume24hUsd(marketSummary: RawMarketSummary) {
  return formatUsd(marketSummary.volume_24h, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}
